export interface ActivityStep {
  kind?: string;
  detail?: string | null;
  // Correlates a tool call with the arguments that arrive once it completes.
  ref?: string | null;
  args?: string | null;
  at?: number | null;
  done_at?: number | null;
}

export interface RenderedStep {
  label: string;
  detail: string;
  duration: string;
  live: boolean;
}

const MAX_ARGS_LENGTH = 50;
const MAX_REASONING_LENGTH = 90;
const MAX_SUMMARY_LENGTH = 90;

// Tools are named <verb>_<resource>; the verb is what the user cares about.
const VERBS: Record<string, string> = {
  get: "Read",
  list: "Read",
  describe: "Read",
  search: "Searched",
  estimate: "Estimated",
  preview: "Previewed",
  compare: "Compared",
  render: "Rendered",
  lookup: "Looked up",
  query: "Queried",
  create: "Created",
  add: "Added",
  update: "Updated",
  delete: "Deleted",
  move: "Moved",
  duplicate: "Duplicated",
  resolve: "Resolved",
  reply: "Replied to",
  upsert: "Saved",
  set: "Set",
  generate: "Generated",
  decide: "Decided on",
  run: "Ran",
};

const ASK_USER = "ask_user";
const PROGRAMMING_CHECK_TOOLS = ["run_programming_check", "get_programming_check"];

export function callsProgrammingCheck(steps: ActivityStep[] | null | undefined): boolean {
  return (steps ?? []).some((step) => step.kind === "tool_call" && PROGRAMMING_CHECK_TOOLS.includes(step.detail ?? ""));
}

function verbOf(tool: string): string {
  return VERBS[tool.split("_")[0]] ?? "Used";
}

function resourceOf(tool: string): string {
  const rest = tool.split("_").slice(1).join(" ");
  return rest || tool.replace(/_/g, " ");
}

/**
 * The arguments are what tells two calls to the same tool apart, so they belong
 * in the step. Only short scalars are shown — a create_card payload would
 * otherwise flood the line.
 */
const IDENTIFIER_KEY = /(^|_)(token|id|ids)$/;

function formatArgs(args?: string | null): string {
  if (!args) return "";

  let parsed: unknown;
  try {
    parsed = JSON.parse(args);
  } catch {
    return "";
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return "";

  const pairs = Object.entries(parsed as Record<string, unknown>)
    .filter(([key, value]) => !IDENTIFIER_KEY.test(key) && ["string", "number", "boolean"].includes(typeof value))
    .map(([, value]) => String(value))
    .filter((value) => value.length > 0 && value.length < MAX_ARGS_LENGTH);
  if (!pairs.length) return "";

  const joined = pairs.join(" · ");
  return joined.length > MAX_ARGS_LENGTH ? `${joined.slice(0, MAX_ARGS_LENGTH)}…` : joined;
}

function formatDuration(step: ActivityStep): string {
  if (step.at == null || step.done_at == null) return "";

  const seconds = (step.done_at - step.at) / 1000;
  if (seconds < 0.1) return "";
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${String(Math.round(seconds % 60)).padStart(2, "0")}s`;
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function reasoningHeadline(text?: string | null): string {
  const source = (text || "").trim();
  const title = source.match(/^\*\*([^*]+)\*\*/)?.[1] ?? source;
  const plain = title.replace(/[*`]+/g, "").replace(/\s+/g, " ").trim();
  return truncate(plain, MAX_REASONING_LENGTH);
}

export function renderActivityStep(step: ActivityStep, isStreaming = false): RenderedStep {
  const duration = formatDuration(step);

  switch (step.kind) {
    case "tool_call": {
      const tool = step.detail || "";
      if (tool === ASK_USER) return { label: "Asked", detail: "for your input", duration: "", live: false };
      const args = formatArgs(step.args);
      return {
        label: tool ? verbOf(tool) : "Read",
        detail: [tool ? resourceOf(tool) : "Episto data", args].filter(Boolean).join(" · "),
        duration,
        // A call still without its arguments is the one running right now.
        live: isStreaming && step.done_at == null,
      };
    }
    case "tool_discovery":
      return { label: "Listed", detail: "the available tools", duration, live: false };
    default:
      return { label: "Thought", detail: reasoningHeadline(step.detail), duration, live: false };
  }
}

export function renderActivitySteps(steps: ActivityStep[] | null | undefined, isStreaming = false): RenderedStep[] {
  return (steps ?? [])
    .filter((step) => step.detail !== ASK_USER)
    .map((step) => renderActivityStep(step, isStreaming))
    .filter((step) => step.detail || step.label);
}

function quantify(resource: string, count: number): string {
  if (count > 1) return `${count} ${resource.endsWith("s") ? resource : `${resource}s`}`;
  if (resource.endsWith("s")) return resource;
  return /^[aeiou]/i.test(resource) ? `an ${resource}` : `a ${resource}`;
}

function joinParts(parts: string[]): string {
  if (parts.length < 2) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * The one line shown while the timeline is collapsed: what the turn did, by
 * verb, in the order the tools were called.
 */
export function summarizeActivity(steps: ActivityStep[] | null | undefined): string {
  const calls = (steps ?? []).filter((step) => step.kind === "tool_call" && step.detail && step.detail !== ASK_USER);
  if (!calls.length) return (steps ?? []).length ? "Thought it through" : "";

  const byVerb = new Map<string, Map<string, number>>();
  calls.forEach((step) => {
    const tool = step.detail as string;
    const resources = byVerb.get(verbOf(tool)) ?? new Map<string, number>();
    resources.set(resourceOf(tool), (resources.get(resourceOf(tool)) ?? 0) + 1);
    byVerb.set(verbOf(tool), resources);
  });

  const clauses = [...byVerb.entries()].map(
    ([verb, resources]) => `${verb} ${joinParts([...resources].map(([resource, count]) => quantify(resource, count)))}`,
  );
  return truncate(clauses.join(" · "), MAX_SUMMARY_LENGTH);
}
